/**
 * Type helper to assert that an object is a Record with values of type T.
 *
 * Usage:
 *   const obj = satisfiesRecord<MyType>()({ a: ..., b: ... });
 *   // obj is inferred as Record<"a" | "b", MyType>
 *
 */
export const satisfiesRecord =
  <T>() =>
  <K extends PropertyKey>(rec: Record<K, T>) =>
    rec;

/**
 * Type helper to assert that an object is a Record with keys of type T.
 *
 * Usage:
 *   const obj = satisfiesKey<string>()({ foo: 1, bar: 2 });
 *   // obj is inferred as Record<string, 1 | 2>
 *
 */
export const satisfiesKey =
  <T extends PropertyKey>() =>
  <B>(rec: Record<T, B>) =>
    rec;

/**
 * Compile-time check that T satisfies Constraint.
 * TypeScript errors at the type alias definition if T doesn't satisfy the shape.
 *
 * Usage:
 *   export type Check = SatisfiesShape<{a: string} | {b: number}, {a?: string, b?: number}>;
 */
export type SatisfiesShape<Constraint, T extends Constraint> = T;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type ShapeSatisfies<Constraint, T extends Constraint> = Constraint;
